import { portalApi, TAGS } from "api/rtkApi";

import type { CountryCode } from "models/CountryCode";
import type { UUID } from "utils";

export type CustomerNames = Record<UUID, string>;

type NamesResponse = {
  customerNames: CustomerNames;
};
type NamesParameters = {
  customerIds?: UUID | UUID[];
};

export default interface CustomerAddress {
  id: UUID;
  active: boolean;
  country?: CountryCode;
  postalCode?: string;
  street?: string;
  buildingNumber?: string;
  state?: string;
  city?: string;
  addressLine2?: string;
}

export type GetCustomerResponse = {
  id: UUID;
  active: boolean;
  address: CustomerAddress | null;
  aisPreferences: unknown;
  children: GetCustomerResponse[];
  circuitBreakerEnabled: boolean;
  companyRegistrationNumber: string;
  countryCode: CountryCode;
  createdAt: string;
  documentNumber: number;
  incorporationDate: string;
  legalName: string;
  merchantCategoryCode: string;
  metadata: unknown | null;
  name: string;
  parent: UUID | null;
  parentCustomer: { id: UUID; name: string };
  terminateAt: string;
};

const CUSTOMER_NAMES_LIMIT = 1000;
const CUSTOMER_NAMES_PAGE = 1;

const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerNames: builder.query<NamesResponse, NamesParameters>({
      query: ({ customerIds }) => ({
        url: "customers",
        method: "GET",
        params: {
          limit: CUSTOMER_NAMES_LIMIT,
          page: CUSTOMER_NAMES_PAGE,
          id: customerIds,
        },
      }),
      transformResponse: (data: GetCustomerResponse[]) => {
        const customerNames = data.reduce<CustomerNames>(
          (names, currentCustomer) => {
            if (currentCustomer?.id && !names[currentCustomer.id]) {
              names[currentCustomer.id] = currentCustomer.name;
            }

            return names;
          },
          {}
        );

        return {
          customerNames,
        };
      },
      providesTags: [TAGS.CUSTOMER_NAMES],
    }),
  }),
  overrideExisting: false,
});

export const { useGetCustomerNamesQuery } = extendedApi;
